import i18n from '../../i18n';

const DISCOVERY_URL =
    (process.env.REACT_APP_DISCOVERY_SERVICE_BASE_URL || '') + '/graphql';

export interface ServiceProfessional {
    id: string;
    name: string;
}

export interface PublicServiceLite {
    id: string;
    name: string;
}

/**
 * The services a business actually exposes for public online booking — the
 * SAME list the consumer sees on /b/<slug>. The share modal must build links
 * only from these (the admin's full service list can include services that
 * aren't publicly bookable, producing links that 404 on the consumer side).
 * Returns [] on error / unknown slug.
 */
export async function getPublicServices(slug: string): Promise<PublicServiceLite[]> {
    try {
        const res = await fetch(DISCOVERY_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': i18n.language || 'pt',
            },
            body: JSON.stringify({
                query: `query businessProfile($slug: ID!) {
                    businessProfile(slug: $slug) { services { id name } }
                }`,
                variables: { slug },
            }),
        });
        const json = await res.json();
        if (json.errors) {
            console.warn(
                '[discovery-service] businessProfile errors:',
                json.errors.map((e: { message: string }) => e.message).join(', ')
            );
            return [];
        }
        return (json.data?.businessProfile?.services as PublicServiceLite[]) ?? [];
    } catch (err) {
        console.warn('[discovery-service] getPublicServices failed:', err);
        return [];
    }
}

/**
 * Returns the professionals available for online booking for a given service
 * on a given business slug. This calls the public discovery-service — no auth
 * token is required.
 *
 * The `id` field is an opaque token (not a numeric team-member id); it maps
 * directly to the `?member=<token>` query param on the consumer booking URL.
 *
 * Returns an empty array on network errors or when no professionals are found.
 */
export async function getServiceProfessionals(
    slug: string,
    serviceId: string
): Promise<ServiceProfessional[]> {
    try {
        const res = await fetch(DISCOVERY_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': i18n.language || 'pt',
            },
            body: JSON.stringify({
                query: `query serviceProfessionals($slug: ID!, $serviceId: ID!) {
                    serviceProfessionals(slug: $slug, serviceId: $serviceId) {
                        id
                        name
                    }
                }`,
                variables: { slug, serviceId },
            }),
        });
        const json = await res.json();
        if (json.errors) {
            console.warn(
                '[discovery-service] serviceProfessionals errors:',
                json.errors.map((e: { message: string }) => e.message).join(', ')
            );
            return [];
        }
        return (json.data?.serviceProfessionals as ServiceProfessional[]) ?? [];
    } catch (err) {
        console.warn('[discovery-service] fetch failed:', err);
        return [];
    }
}
